import { AlertCircleIcon } from "lucide-react";
import { useEffect, useRef } from "react";
import { cn } from "../lib/cn";

interface DialogErrorProps {
  /** The translated sentence, or `null`/`undefined` when the last attempt did not fail. */
  message?: string | null;
  className?: string;
}

/**
 * Why the dialog's action did not go through, said in the dialog (US-37.4).
 *
 * <p>It belongs directly above {@link DialogFooter}: that is where the eye already is when the button
 * fails to do anything, and it is inside the scroll container, so a long form scrolls to it rather
 * than past it. `role="alert"` makes a screen reader announce it without moving focus, which matters
 * because focus is still on the button the person just pressed.
 *
 * <p>It renders nothing at all when there is no message, so a dialog can place it unconditionally
 * and not grow a gap in the layout for the ninety-nine times out of a hundred that nothing failed.
 */
export function DialogError({ message, className }: DialogErrorProps) {
  const box = useRef<HTMLParagraphElement>(null);
  // Brought into view whenever a new sentence arrives: on a phone the footer sticks to the bottom of
  // the dialog, so the button can be pressed with the end of the form - and this - scrolled away.
  // `scroll-mb-24` keeps it clear of that footer rather than tucked under it.
  useEffect(() => {
    if (message) box.current?.scrollIntoView({ block: "nearest" });
  }, [message]);
  if (!message) {
    return null;
  }
  return (
    <p
      ref={box}
      role="alert"
      data-testid="dialog-error"
      className={cn(
        "flex scroll-mb-24 items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive",
        className,
      )}
    >
      <AlertCircleIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span>{message}</span>
    </p>
  );
}
